import { Service, signal } from '@angular/core';
import { Report } from './report';

const STORAGE_KEY = 'mce.recentReports.v1';
// Api2Pdf retains files for 24 hours; leave a margin for generation and clock differences.
const REPORT_LIFETIME_MS = (24 * 60 - 5) * 60 * 1000;

export interface RecentReportInput {
  readonly userUid: string;
  readonly projectUid: string;
  readonly report: Report;
  readonly name: string;
  readonly url: string;
  /** UTC milliseconds, captured when generation starts rather than after billing completes. */
  readonly generatedAt: number;
}

export interface RecentReport extends RecentReportInput {
  readonly expiresAt: number;
}

const reportTypes = new Set(Object.values(Report).filter(value => typeof value === 'number'));

function isRecentReport(value: unknown): value is RecentReport {
  if (!value || typeof value !== 'object') return false;
  const entry = value as Partial<RecentReport>;
  return typeof entry.userUid === 'string' && entry.userUid.trim().length > 0
    && typeof entry.projectUid === 'string' && entry.projectUid.trim().length > 0
    && typeof entry.name === 'string' && entry.name.trim().length > 0
    && typeof entry.report === 'number' && reportTypes.has(entry.report)
    && typeof entry.generatedAt === 'number' && Number.isFinite(entry.generatedAt)
    && typeof entry.expiresAt === 'number' && Number.isFinite(entry.expiresAt)
    && entry.expiresAt === entry.generatedAt + REPORT_LIFETIME_MS
    && isReportUrl(entry.url);
}

function isReportUrl(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && !url.username && !url.password;
  } catch {
    return false;
  }
}

@Service()
export class RecentReportsService {
  private readonly entries = signal<readonly RecentReport[]>([]);

  constructor() {
    let stored: unknown = [];
    try {
      stored = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    } catch {
      // Invalid JSON or unavailable storage must not prevent report generation.
    }
    this.entries.set(Array.isArray(stored) ? stored.filter(isRecentReport) : []);
    this.refresh();
  }

  /** Call when opening the Reports menu to prune expired links without polling. */
  refresh(): void {
    const now = Date.now();
    this.entries.update(entries => entries
      .filter(entry => entry.generatedAt <= now && entry.expiresAt > now)
      .sort((a, b) => b.generatedAt - a.generatedAt));
    this.persist();
  }

  /** Signal-backed selection; also checks expiry when called before reopening a link. */
  reportsFor(userUid: string | null | undefined, projectUid: string | null | undefined): readonly RecentReport[] {
    if (!userUid || !projectUid) return [];
    const now = Date.now();
    return this.entries().filter(entry =>
      entry.userUid === userUid && entry.projectUid === projectUid && entry.expiresAt > now);
  }

  /** Record only after generation and charging have both succeeded. Does not call the API. */
  add(report: RecentReportInput): void {
    this.refresh();
    const entry: RecentReport = { ...report, expiresAt: report.generatedAt + REPORT_LIFETIME_MS };
    const now = Date.now();
    if (!isRecentReport(entry) || entry.generatedAt > now || entry.expiresAt <= now) return;
    this.entries.update(entries => [entry, ...entries.filter(existing =>
      existing.userUid !== entry.userUid || existing.projectUid !== entry.projectUid || existing.url !== entry.url)]
      .sort((a, b) => b.generatedAt - a.generatedAt));
    this.persist();
  }

  private persist(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.entries()));
    } catch {
      // Keep the current session usable when storage is blocked or its quota is exceeded.
    }
  }
}
